import { useEffect, useRef } from "react";
import lottie from "lottie-web";
import wakeUpAnimation from "../assets/json/Wake_up.json";
import { Button } from "./Button";

const APP_STORE =
  "https://apps.apple.com/app/apple-store/id6746773849?pt=127885797&ct=website&mt=8";

function AppleMark({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      aria-hidden
    >
      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
    </svg>
  );
}

const floaters = [
  {
    src: "/hero/01-playing-cards.jpg",
    wrap: "left-[4%] top-[10%] h-20 w-20 -rotate-6 sm:h-24 sm:w-24 md:left-[6%] md:top-[12%]",
    delay: "0s",
  },
  {
    src: "/hero/02-party-lights.jpg",
    wrap: "right-[5%] top-[14%] h-24 w-24 rotate-6 sm:h-28 sm:w-28 md:right-[8%] md:top-[10%]",
    delay: "1.2s",
  },
  {
    src: "/hero/03-board-games.jpg",
    wrap: "left-[2%] top-[42%] hidden h-28 w-28 rotate-3 sm:block md:left-[4%] md:top-[38%]",
    delay: "0.4s",
  },
  {
    src: "/hero/04-friends-group.jpg",
    wrap: "right-[3%] top-[40%] hidden h-24 w-24 -rotate-3 sm:block md:right-[5%] md:top-[36%]",
    delay: "2s",
  },
  {
    src: "/hero/05-drinks-cheers.jpg",
    wrap: "bottom-[18%] left-[8%] h-20 w-20 -rotate-12 sm:h-24 sm:w-24 md:bottom-[22%] md:left-[10%]",
    delay: "0.8s",
  },
  {
    src: "/hero/06-poker-chips.jpg",
    wrap: "bottom-[14%] right-[6%] h-20 w-20 rotate-6 sm:h-28 sm:w-28 md:bottom-[18%] md:right-[10%]",
    delay: "1.6s",
  },
] as const;

export function HeroHYE() {
  const lottieRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!lottieRef.current) return;

    const animation = lottie.loadAnimation({
      container: lottieRef.current,
      renderer: "svg",
      loop: false,
      autoplay: true,
      animationData: wakeUpAnimation,
    });

    return () => animation.destroy();
  }, []);

  return (
    <section className="relative z-10 overflow-hidden bg-gradient-to-b from-[#0B1117] to-[#151519]">
      <div className="pointer-events-none absolute inset-0 hidden md:block">
        {floaters.map(({ src, wrap, delay }, i) => (
          <div
            key={i}
            className={`absolute ${wrap}`}
            aria-hidden
          >
            <div
              className="hero-float relative h-full w-full overflow-hidden rounded-2xl border border-white/15 shadow-[0_20px_50px_rgba(0,0,0,0.35)]"
              style={{ animationDelay: delay }}
            >
              <img
                src={src}
                alt=""
                className="h-full w-full object-cover"
                width={600}
                height={600}
                loading="eager"
                decoding="async"
              />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-white/[0.07] to-transparent" />
            </div>
          </div>
        ))}
      </div>

      <div className="relative mx-auto flex min-h-[min(78dvh,720px)] max-w-6xl flex-col items-center justify-center px-4 py-10 text-center sm:min-h-[min(90dvh,860px)] sm:px-6 sm:py-12 md:py-16">
        <div className="relative z-10 flex w-full max-w-2xl flex-col items-center">
          <div className="relative mx-auto h-36 w-full max-w-md overflow-hidden sm:h-44 md:h-52">
            <div
              ref={lottieRef}
              className="mx-auto h-[17rem] w-full -translate-y-6 sm:h-[22rem] sm:-translate-y-8 md:h-[24rem] md:-translate-y-10"
              aria-hidden
            />
          </div>

          <h1 className="mt-1 text-balance text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl md:text-6xl lg:text-7xl">
            Cards Against Humanity
            <br />
            but for your camera roll.
          </h1>

          <p className="mt-6 max-w-lg text-base text-white/70 md:text-lg">
            A quick 3 to 12 player party game—everyone submits a real photo from
            their camera roll, then votes on the funniest or best match to the
            prompt.
          </p>

          <Button
            id="get-started"
            variant="orange"
            pad="custom"
            href={APP_STORE}
            className="mt-8 px-6 py-3 text-sm font-semibold shadow-[0_12px_40px_rgba(0,0,0,0.45)] sm:mt-10 sm:px-7 sm:py-3.5 sm:text-base"
          >
            <AppleMark className="h-4 w-4 sm:h-[1.125rem] sm:w-[1.125rem]" />
            Download
          </Button>
        </div>
      </div>
    </section>
  );
}
